import {
  icon_axis_default,
  icon_axis_selected,
  icon_box_3d_default,
  icon_box_3d_selected,
  icon_mesh_off,
  icon_mesh_on,
  icon_mono_default,
  icon_mono_selected,
  icon_view_behind,
  icon_view_bottom,
  icon_view_front,
  icon_view_left,
  icon_view_right,
  icon_view_top,
} from 'components/base/images';
import CustomLine from 'components/common/line/CustomLine';
import useModel from 'lib/hooks/useModel';
import { TInputState } from 'lib/types/common';
import { IView, TAlign } from 'lib/types/model';
import React from 'react';
import styled from 'styled-components';
import * as THREE from 'three';

interface ModelOptionsProps {
  groupRef: React.MutableRefObject<THREE.Group | null>;
  cameraAlign: TInputState<TAlign>;
  isAnimated: TInputState<boolean>;
  isAxesHelper: TInputState<boolean>;
  isWireframeView: TInputState<boolean>;
  isMonoScanView: TInputState<boolean>;
  isViewBoxOpen: TInputState<boolean>;
}

function ModelOptions({
  groupRef,
  cameraAlign,
  isAnimated,
  isAxesHelper,
  isWireframeView,
  isMonoScanView,
  isViewBoxOpen,
}: ModelOptionsProps) {
  const { addWireFrame, removeWireFrame } = useModel();

  const handleAlignManipulator = (align: TAlign) => {
    cameraAlign.setValue(align);
  };

  const handleAxesHelper = () => {
    isAxesHelper.setValue(!isAxesHelper.value);
  };

  const handleMonoScanView = () => {
    groupRef?.current?.children?.map((child: THREE.Object3D<THREE.Event>) => {
      if (child instanceof THREE.Mesh) {
        const material = child.material as THREE.ShaderMaterial;

        if (material.uniforms.extension.value !== 'ply') return;

        // material.vertexColors = isMonoScanView.value;
        material.uniforms.isVertexColor.value = isMonoScanView.value;
        // material.uniforms.frontColor.value = new THREE.Vector4(0.82, 0.71, 0.55, 1);
      }
    });

    isMonoScanView.setValue(!isMonoScanView.value);
  };

  const handleWireframeView = () => {
    groupRef?.current?.children?.map((child: THREE.Object3D<THREE.Event>) => {
      if (child instanceof THREE.Mesh) {
        if (isWireframeView.value) {
          removeWireFrame(child);
        } else {
          addWireFrame(child);
        }
      }
    });

    isWireframeView.setValue(!isWireframeView.value);
  };

  const handleViewOpen = () => {
    isViewBoxOpen.setValue(!isViewBoxOpen.value);
  };

  return (
    <StyledModelOptions>
      <div className="modelOptions__container">
        <StyledOptionBox className="horizontal">
          <button className="view_item" onClick={handleAxesHelper}>
            <img src={isAxesHelper.value ? icon_axis_selected : icon_axis_default} />
          </button>
          <CustomLine type="vertical" size={40} />
          <button className="view_item" onClick={handleWireframeView}>
            <img src={isWireframeView.value ? icon_mesh_on : icon_mesh_off} />
          </button>
          <CustomLine type="vertical" size={40} />
          <button className="view_item" onClick={handleMonoScanView}>
            <img src={isMonoScanView.value ? icon_mono_selected : icon_mono_default} />
          </button>
        </StyledOptionBox>

        <div className="modelOptions__view_box">
          {isViewBoxOpen.value && (
            <StyledOptionBox className="horizontal">
              <button
                className="view_item"
                onClick={() => !isAnimated.value && handleAlignManipulator('front')}
              >
                <img src={icon_view_front} />
              </button>
              <CustomLine type="vertical" size={40} />
              <button
                className="view_item"
                onClick={() => !isAnimated.value && handleAlignManipulator('back')}
              >
                <img src={icon_view_behind} />
              </button>
              <CustomLine type="vertical" size={40} />
              <button
                className="view_item"
                onClick={() => !isAnimated.value && handleAlignManipulator('left')}
              >
                <img src={icon_view_left} />
              </button>
              <CustomLine type="vertical" size={40} />
              <button
                className="view_item"
                onClick={() => !isAnimated.value && handleAlignManipulator('right')}
              >
                <img src={icon_view_right} />
              </button>
              <CustomLine type="vertical" size={40} />
              <button
                className="view_item"
                onClick={() => !isAnimated.value && handleAlignManipulator('top')}
              >
                <img src={icon_view_top} />
              </button>
              <CustomLine type="vertical" size={40} />
              <button
                className="view_item"
                onClick={() => !isAnimated.value && handleAlignManipulator('bottom')}
              >
                <img src={icon_view_bottom} />
              </button>
            </StyledOptionBox>
          )}
          <StyledOptionBox className="horizontal">
            <button className="view_item" onClick={handleViewOpen}>
              <img src={isViewBoxOpen.value ? icon_box_3d_selected : icon_box_3d_default} />
            </button>
          </StyledOptionBox>
        </div>
      </div>
    </StyledModelOptions>
  );
}

const StyledModelOptions = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  .modelOptions__container {
    display: flex;
    width: 100%;
    height: 100%;
    padding: 20px;
    justify-content: space-between;
    align-items: flex-end;
    .modelOptions__view_box {
      display: flex;
      flex-direction: column;
      row-gap: 10px;
    }
  }
`;

const StyledOptionBox = styled.div`
  display: flex;
  background-color: white;
  padding: 3px;
  border-radius: 10px;
  box-shadow: 0px 0px 15px 0px rgba(0, 0, 0, 0.1);
  z-index: 1;
  align-items: center;
  &.horizontal {
    width: 60px;
    flex-direction: column;
    row-gap: 10px;
  }
  &.vertical {
    height: 60px;
    column-gap: 10px;
  }
  .view_item {
    display: flex;
    justify-content: center;
    border-radius: 8px;
    padding: 7px;
    width: 100%;
    height: 100%;
    transition: background-color 0.1s ease-in-out;
    &:active {
      animation: fadeIn 0.3s forwards;
      background-color: transparent;
    }
    &:not(:active) {
      animation: fadeOut 0.3s forwards;
      background-color: transparent;
    }

    @keyframes fadeIn {
      from {
        background-color: rgba(0, 166, 226, 0);
      }
      to {
        background-color: rgba(0, 166, 226, 0.15);
      }
    }

    @keyframes fadeOut {
      from {
        background-color: rgba(0, 166, 226, 0.15);
      }
      to {
        background-color: rgba(0, 166, 226, 0);
      }
    }
  }
`;

export default ModelOptions;
